<script setup>
import { ref, watch, computed } from "vue";
import { useDebounceFn } from "@vueuse/core";
import CommonSwatch from "../common/Swatch.vue";
import { IonIcon } from "@ionic/vue";
import { closeCircle } from "ionicons/icons";

const props = defineProps({
	sizes: {
		type: Array,
		required: true,
	},
	colourBasket: Object,
});

const stockErrorMessage = ref(null);
const basket = defineModel();

const getCurrentQtyForSize = (sizeId) => {
	return props.colourBasket.products.find(
		(product) => product.size_id === sizeId
	);
};

const findSizIdInSizes = (size) => {
	if (!size.size_slug) return;

	return props.sizes.find(
		(s) => s.slug.toLowerCase() === size.size_slug.toLowerCase()
	).id;
};

const stockMessage = (stock) =>
	computed(() => {
		if (stock === 0) return "Out of Stock";
		if (stock < 25) return "Low in Stock";
		return "In stock";
	});

const handleUpdateQty = (currentColourInBasket, size, event) => {
	var newQtyLevel =
		event.target && event.target.value ? event.target.value : event;

	if (typeof newQtyLevel === "object") return;
	if (newQtyLevel.length === 0) return;

	const sizeId = findSizIdInSizes(size);
	const currentSizeInBasket = getCurrentQtyForSize(sizeId);

	// const maxQtyForSizeVariation = getStockBySizeSlug(size.size_slugs);

	if (newQtyLevel <= 0) {
		if (currentSizeInBasket) {
			const indexToRemove = currentColourInBasket.products.findIndex(
				(product) => product.size_id === sizeId
			);
			currentColourInBasket.products.splice(indexToRemove, 1);
		}

		return;
	}

	// Check to ensure ammount is not more than max stock
	// if (newQtyLevel > maxQtyForSizeVariation) {
	// 	newQtyLevel = maxQtyForSizeVariation;
	// 	event.target.value = newQtyLevel;
	// 	stockErrorMessage.value = `The max quantity for ${size.name} is ${maxQtyForSizeVariation}`;
	// }

	if (currentSizeInBasket) {
		currentSizeInBasket.qty = newQtyLevel;
	} else {
		currentColourInBasket.products.push({
			size_id: sizeId,
			variation_id: getVariationId(size.size_slug),
			qty: newQtyLevel,
		});
	}
};

const handleRemoveProductFromBasket = (currentColourInBasket) => {
	const indexToRemove = basket.value.products.findIndex(
		(product) => product.id === currentColourInBasket.id
	);
	basket.value.products.splice(indexToRemove, 1);
};

const getStockBySizeSlug = (sizeSlug) => {
	const stock = props.colourBasket.stock.find(
		(stock) => stock.size_slug === sizeSlug
	);

	const stockLevel = stock && stock.stock ? stock.stock : 100000;
	return stockLevel !== -1 ? stockLevel : 100000;
};

const getVariationId = (sizeSlug) => {
	const stock = props.colourBasket.stock.find(
		(stock) => stock.size_slug === sizeSlug
	);
	return stock && stock.variation_id ? stock.variation_id : 0;
};

const reduceQty = (size) => {
	const sizeId = findSizIdInSizes(size);
	const currentSizeInBasket = getCurrentQtyForSize(sizeId);
	let newQty = 0;

	if (
		currentSizeInBasket &&
		currentSizeInBasket.qty &&
		currentSizeInBasket.qty > 0
	) {
		newQty = currentSizeInBasket.qty - 1;
	}

	handleUpdateQty(props.colourBasket, size, newQty);
};

const increaseQty = (size) => {
	const sizeId = findSizIdInSizes(size);
	const currentSizeInBasket = getCurrentQtyForSize(sizeId);
	let newQty = 1;

	if (currentSizeInBasket && currentSizeInBasket.qty) {
		newQty = currentSizeInBasket.qty + 1;
	}

	handleUpdateQty(props.colourBasket, size, newQty);
};

const qtyBySize = (size) => {
	if (!size.size_slug) return 0;
	return getCurrentQtyForSize(findSizIdInSizes(size))
		? getCurrentQtyForSize(findSizIdInSizes(size)).qty
		: 0;
};

watch(stockErrorMessage, () => {
	if (!stockErrorMessage.value) return;
	setTimeout(() => {
		stockErrorMessage.value = null;
	}, 3000);
});
</script>

<template>
	<div>
		<template v-if="colourBasket">
			<div class="u-flex u-space-between u-v-center">
				<span class="u-flex u-gap-half u-v-center">
					<CommonSwatch :data="colourBasket" />
					{{ colourBasket.name }}
				</span>

				<span
					class="e-link e-link--grey u-flex u-gap-quarter u-v-center"
					@click="handleRemoveProductFromBasket(colourBasket)"
					>Remove <ion-icon class="u-font-large" :icon="closeCircle"
				/></span>
			</div>
		</template>

		<template v-if="stockErrorMessage">
			<div
				class="c-card c-card--error u-my-half u-px u-py-half u-font-small u-tac"
			>
				{{ stockErrorMessage }}
			</div>
		</template>

		<div class="o-layout o-layout--gutter-small u-mt u-gap-y">
			<template v-for="(size, index) in colourBasket.stock" :key="index">
				<div class="o-layout__item u-width-1/4">
					<div class="u-flex-even@ipad u-tac">
						<span class="u-block u-mb-half u-font-small u-uppercase">
							{{ size.size_name }}
						</span>

						<div class="u-qty-placeholder">
							<div class="u-pos-rel" v-if="getStockBySizeSlug(size.slug) !== 0">
								<span
									class="e-qty__buttons e-qty__buttons--minus"
									@click="reduceQty(size)"
								>
									<ion-icon name="remove"></ion-icon>
								</span>
								<input
									:max="getStockBySizeSlug(size.slug)"
									type="number"
									@keyup="
										useDebounceFn(
											handleUpdateQty(colourBasket, size, $event),
											400
										)
									"
									class="e-qty u-tac"
									:value="qtyBySize(size)"
								/>
								<span
									class="e-qty__buttons e-qty__buttons--plus"
									@click="increaseQty(size)"
								>
									<ion-icon name="add"></ion-icon>
								</span>
							</div>
							<input
								v-else
								type="number"
								disabled
								class="e-qty e-qty--disabled u-tac"
								value="0"
							/>
						</div>

						<span
							class="u-block u-mt-half u-font-small"
							:class="{
								'u-text-colour-error': getStockBySizeSlug(size.slug) < 25,
							}"
						>
							{{ stockMessage(getStockBySizeSlug(size.slug)) }}
						</span>
					</div>
				</div>
			</template>
		</div>
	</div>
</template>
